import { useRef, useCallback } from 'react'
import { MapboxScene, type MapboxSceneRefs } from '@/components/3d'
import type { MapboxCustomLayerService } from '@/services/3d'
import { mockHangar, OPA_LOCKA_CAMERA } from './data/hangarConfig'
import { baselineFleet } from './data/fleetConfig'
import { SCENARIO_METRICS } from './data/metricsData'
import { useDemoOrchestrator } from './hooks/useDemoOrchestrator'
import { ScenarioPanel } from './components/ScenarioPanel'
import { MetricsDashboard } from './components/MetricsDashboard'
import { TimelineStrip } from './components/TimelineStrip'

export function PlannerDemoPage() {
  const layerServiceRef = useRef<MapboxCustomLayerService | null>(null)

  const {
    demoState,
    activeScenarioId,
    selectedStrategy,
    departureWindow,
    onSceneReady,
    triggerIncoming,
    showStrategySelect,
    selectStrategy,
    adjustDeparture,
    resetDemo,
  } = useDemoOrchestrator(layerServiceRef)

  const handleSceneReady = useCallback(
    async (refs: MapboxSceneRefs, _aircraftData: unknown) => {
      layerServiceRef.current = refs.layerService

      // Load baseline fleet imperatively — no DB dependency
      if (refs.layerService) {
        for (const aircraft of baselineFleet) {
          await refs.layerService.addAircraft(aircraft)
        }
      }

      onSceneReady()
    },
    [onSceneReady]
  )

  return (
    <div className="w-screen h-screen overflow-hidden bg-slate-950 relative">
      {/* ── 3D MAP ── */}
      <MapboxScene
        className="w-full h-full"
        buildings={[mockHangar]}
        initialViewSettings={OPA_LOCKA_CAMERA}
        enableShadows={false}
        interactionEnabled={false}
        onReady={handleSceneReady}
        renderOverlay={(_map, _aircraftData, _loaded3DIds) => (
          <div className="absolute inset-0 pointer-events-none">
            {/* Top timeline */}
            <TimelineStrip />

            {/* Right scenario panel */}
            <ScenarioPanel
              demoState={demoState}
              activeScenarioId={activeScenarioId}
              selectedStrategy={selectedStrategy}
              departureWindow={departureWindow}
              onShowIncoming={triggerIncoming}
              onShowStrategySelect={showStrategySelect}
              onSelectStrategy={selectStrategy}
              onAdjustDeparture={adjustDeparture}
              onReset={resetDemo}
            />

            {/* Bottom metrics bar */}
            <MetricsDashboard
              scenarioId={activeScenarioId}
              metrics={SCENARIO_METRICS[activeScenarioId]}
              visible={demoState === 'result' || demoState === 'animating'}
            />
          </div>
        )}
      />

      {/* Loading overlay */}
      {demoState === 'loading' && (
        <div className="absolute inset-0 bg-slate-950 flex items-center justify-center z-20">
          <div className="text-center space-y-3">
            <div className="w-10 h-10 border-2 border-sky-500/30 border-t-sky-400 rounded-full animate-spin mx-auto" />
            <div className="text-white/60 text-sm">Loading 3D scene…</div>
          </div>
        </div>
      )}

      {/* Demo mode badge */}
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <div className="bg-black/60 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-1.5">
          <div className="text-[10px] text-white/30 uppercase tracking-widest font-semibold">
            Hangar Intelligence
          </div>
          <div className="text-sm font-bold text-white">5D Planner — Sales Demo</div>
        </div>
      </div>
    </div>
  )
}
